import { useApprovedUsers } from "@/composables/gallery/useApprovedUsers";
import {
  GradeLevelEnum,
  SchoolTypeEnum,
  SubjectTypeEnum,
  ToolsUser,
} from "@/services/fobizzToolsApi";
import { gradeLevelKeys, schoolTypeKeys, subjectTypeKeys } from "@/types";
import { FilterCategory, FilterableRoute } from "@/types/filter.types";
import { computed, ComputedRef } from "vue";
import { useI18n } from "vue-i18n";

export const useGalleryCategories = (
  international: ComputedRef<string | undefined>,
  currentFilterableRoute: ComputedRef<FilterableRoute>
) => {
  const { t } = useI18n();
  const { approvedUsers } = useApprovedUsers(international);
  const appsStore = useAppsStore();

  const partnerCategory = computed<FilterCategory>(() => ({
    id: "userIds",
    component: "DropdownCheckbox",
    pages: [FilterableRoute.INDEX],
    props: {
      id: "approvedUserIdsDropdown",
      label: t("materials.labels.approvedUserIds"),
      options:
        approvedUsers.value?.map((user: ToolsUser) => ({
          label: user.publicDisplayName as string,
          value: user.id,
        })) ?? [],
      selectedValues: [],
    },
  }));

  const schoolTypesCategory: FilterCategory = {
    id: "schoolTypes",
    component: "DropdownCheckbox",
    pages: [
      FilterableRoute.INDEX,
      FilterableRoute.COMMUNITY,
      FilterableRoute.COLLECTION_SHOW,
    ],
    props: {
      id: "schoolTypesDropdown",
      label: t("labels.schoolTypes"),
      options: schoolTypeKeys.map((value) => ({
        label: t(
          `enums.schoolTypes.${SchoolTypeEnum[value as SchoolTypeEnum]}`
        ),
        value: value.toString(),
      })),
      selectedValues: [],
    },
  };

  const gradeLevelsCategory: FilterCategory = {
    id: "gradeLevels",
    component: "DropdownCheckbox",
    pages: [
      FilterableRoute.INDEX,
      FilterableRoute.COMMUNITY,
      FilterableRoute.COLLECTION_SHOW,
    ],
    props: {
      id: "gradeLevelsDropdown",
      label: t("labels.gradeLevels"),
      options: gradeLevelKeys.map((value) => ({
        label: t(
          `enums.gradeLevels.${GradeLevelEnum[value as GradeLevelEnum]}`
        ),
        value: value.toString(),
      })),
      selectedValues: [],
    },
  };

  const subjectTypesCategory: FilterCategory = {
    id: "subjectTypes",
    component: "DropdownCheckbox",
    pages: [
      FilterableRoute.INDEX,
      FilterableRoute.COMMUNITY,
      FilterableRoute.TO_TEACH,
      FilterableRoute.COLLECTION_SHOW,
    ],
    props: {
      id: "subjectTypesDropdown",
      label: t("labels.subjectTypes"),
      options: subjectTypeKeys.map((value) => ({
        label: t(
          `enums.subjectTypes.${SubjectTypeEnum[value as SubjectTypeEnum]}`
        ),
        value: value.toString(),
      })),
      selectedValues: [],
    },
  };

  const internationalCategory: FilterCategory = {
    id: "international",
    component: "FilterFormDropdown",
    pages: [
      FilterableRoute.INDEX,
      FilterableRoute.COMMUNITY,
      FilterableRoute.TO_TEACH,
    ],
    props: {
      id: "internationalDropdown",
      label: t("materials.filters.language"),
      options: [
        { value: "true", label: t("materials.filters.international.true") },
        { value: "false", label: t("materials.filters.international.false") },
      ],
      selectedValues: "",
      radio: true,
    },
  };

  const orderCategory: FilterCategory = {
    id: "order",
    component: "FilterFormDropdown",
    pages: [
      FilterableRoute.INDEX,
      FilterableRoute.COMMUNITY,
      FilterableRoute.TO_TEACH,
    ],
    props: {
      id: "order",
      label: t("materials.filters.order.label"),
      options: [
        { value: "created_at", label: t("materials.filters.order.newest") },
        { value: "relevant", label: t("materials.filters.order.relevance") },
      ],
      selectedValues: "",
      radio: true,
    },
  };

  const availableApps = computed(() => {
    const apps =
      currentFilterableRoute.value == FilterableRoute.INDEX
        ? appsStore.listCatalogablePlusAssistant
        : appsStore.listCatalogable;

    apps.map((app) =>
      app.id === "ClassroomScreen::Screen"
        ? { ...app, id: "TeachingBoard::Screen" }
        : app
    );

    const collection = {
      id: "Collection",
      modelName: t("materials.labels.filterCollection"),
      icon: "bi-collection",
    };

    return [...apps, collection];
  });

  const models = computed<FilterCategory>(() => ({
    id: "models",
    component: "DropdownCheckbox",
    pages: [
      FilterableRoute.INDEX,
      FilterableRoute.COMMUNITY,
      FilterableRoute.COLLECTION_SHOW,
    ],
    props: {
      label: t("materials.labels.models"),
      id: "model-buttons",
      options: availableApps.value.map((app) => ({
        value: app.id,
        label: app.modelName,
        id: app.modelName,
        icon: app.icon,
      })),
      selectedValues: [],
    },
  }));

  const categories = computed<FilterCategory[]>(() =>
    [
      partnerCategory.value,
      schoolTypesCategory,
      gradeLevelsCategory,
      subjectTypesCategory,
      models.value,
      internationalCategory,
      orderCategory,
    ].filter((cat: FilterCategory) =>
      cat.pages.includes(currentFilterableRoute.value)
    )
  );

  return { categories };
};
